import { API_URL } from "@/mainconfig"; // Impor instance API_URL dari mainconfig.js
import { getResponseMessage } from "@/utils/responseMapping";
import i18n from "@/plugins/LanguageComponent.js";

const state = {
    roomCart: JSON.parse(localStorage.getItem("roomCart")) || {}, // Data item yang ada di keranjang
    loading: false, // Status loading untuk proses
    error: null, // Error jika ada masalah
    errorKey: null, // Key error untuk i18n
    errorMessage: null, // Pesan error terjemahan
    isFromCart: false, // Default false
};

const getters = {
    isLoading: (state) => state.loading,
    getError: (state) => state.errorMessage,
    roomCart: (state) => state.roomCart,
    cartCount: (state) => {
        // Hitung jumlah BookDetails dalam roomCart
        return state.roomCart?.BookDetails?.length || 0;
    },
    cartTotal: (state) => {
        // Hitung total harga dari semua BookDetails
        return state.roomCart.BookDetails
            ? state.roomCart.BookDetails.reduce(
                  (total, item) => total + parseFloat(item.Total || 0),
                  0
              )
            : 0;
    },
    isFromCart: (state) => state.isFromCart,
};

const actions = {
    async addToCart({ commit }, payload) {
        commit("setLoading", true);

        try {
            const response = await API_URL.post("/booking/cart", payload, {
                requiresAuth: true, // Opsi requiresAuth untuk memastikan token disertakan
            });

            // Setelah berhasil menambahkan, fetch data terbaru dari backend
            commit("addItemToCart", response.data.data);

            commit("setError", null);
            return response.data; // Kembalikan respons jika diperlukan
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError"); // Kirim errorKey ke mutation
            console.log("Mapped errorKey:", errorKey); // Debugging hasil mapping
            throw error; // Opsional: Lempar error ke level yang lebih tinggi
        } finally {
            commit("setLoading", false);
        }
    },
    async fetchCart({ commit }) {
        console.log("fetchCart dipanggil"); // Debugging
        commit("setLoading", true);
        try {
            const response = await API_URL.get("/booking/cart", {
                requiresAuth: true,
            }); // Ambil data cart dari backend
            commit("addItemToCart", response.data.data); // Set data cart dari respons
        } catch (error) {
            commit("setError", "errors.fetchCartError");
            console.error("Error fetching cart:", error);
        } finally {
            commit("setLoading", false);
        }
    },
    async addRoomToCart({ commit }, payload) {
        commit("setLoading", true);

        try {
            // Ambil BookingId dari state atau localStorage
            const bookingId =
                state.roomCart.Id ||
                JSON.parse(localStorage.getItem("roomCart"))?.Id;

            if (bookingId) {
                payload.BookingId = bookingId; // Tambahkan BookingId ke payload
            }

            // Simpan data ke backend (opsional, jika ada endpoint untuk ini)
            const response = await API_URL.post(
                "/booking/addRoomToCart",
                payload,
                {
                    requiresAuth: true,
                }
            ); // Tambah kamar ke cart

            commit("addItemToCart", response.data.data);
            commit("setError", null);
            return response.data;
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError"); // Kirim errorKey ke mutation
            console.error("Error adding room to cart:", errorKey);
            throw error;
        } finally {
            commit("setLoading", false);
        }
    },
    async clearCart({ commit }) {
        commit("setLoading", true);
        try {
            await API_URL.delete("/booking/clearCart"); // Hapus semua cart di backend
            commit("clearRoomCart");
        } catch (error) {
            commit("setError", "errors.clearCartError");
            console.error("Error clearing cart:", error);
        } finally {
            commit("setLoading", false);
        }
    },
    async removeCartDetail({ commit }, roomId) {
        commit("setLoading", true);

        try {
            const response = await API_URL.delete(
                `/booking/removeCartDetail/${roomId}`
            ); // Hapus item dari backend

            commit("addItemToCart", response.data.data);
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error; // Lempar error kembali ke komponen
        } finally {
            commit("setLoading", false);
        }
    },
    async updateCartDetail({ commit }, payload) {
        commit("setLoading", true);
        try {
            const response = await API_URL.post(
                "/booking/updateCartDetail",
                payload,
                {
                    requiresAuth: true,
                }
            );
            commit("addItemToCart", response.data.data);
        } catch (error) {
            const { status, data } = error.response || {};
            const errorKey = getResponseMessage(status, data?.code);
            commit("setError", errorKey || "errors.defaultError");
            throw error; // Lempar error kembali ke komponen
        } finally {
            commit("setLoading", false);
        }
    },
};

const mutations = {
    setLoading(state, isLoading) {
        state.loading = isLoading;
    },
    setError(state, errorKey) {
        state.errorKey = errorKey;
        state.errorMessage = errorKey ? i18n.global.t(errorKey) : null;
    },
    clearError(state) {
        state.errorKey = null;
        state.errorMessage = null;
    },
    setRoomCart(state, roomCart) {
        state.roomCart = roomCart || {}; // Pastikan roomCart adalah object
        localStorage.setItem("roomCart", JSON.stringify(state.roomCart)); // Simpan ke localStorage
    },
    addItemToCart(state, item) {
        state.roomCart = item;
        localStorage.setItem("roomCart", JSON.stringify(state.roomCart)); // Perbarui localStorage
    },
    removeItemFromCartById(state, cartDetailId) {
        state.roomCart = state.roomCart.filter(
            (item) => item.id !== cartDetailId
        );
        localStorage.setItem("roomCart", JSON.stringify(state.roomCart)); // Perbarui localStorage
    },
    clearRoomCart(state) {
        state.roomCart = {}; // Reset ke object kosong
        localStorage.removeItem("roomCart"); // Hapus data dari localStorage
    },
    updateCartItem(state, updatedItem) {
        const index = state.roomCart.findIndex(
            (item) => item.id === updatedItem.id
        );
        if (index !== -1) {
            state.roomCart[index] = updatedItem;
            localStorage.setItem("roomCart", JSON.stringify(state.roomCart)); // Perbarui localStorage
        }
    },
    setIsFromCart(state, payload) {
        state.isFromCart = payload;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
